import React from 'react';
import Layout from '../../../../components/layout/AppLayout';
import Loader from '../../../../../shared/components/ui/Loader';
import ErrorScreen from '../../../../../shared/components/ui/ErrorScreen';
import { errorMessage } from './format';

/**
 * Page frame shared by the daily reports: the sticky header strip used by the
 * operations reports, then the body, or an error screen in its place.
 */
const ReportShell = ({ title, subtitle, actions, filters, loading, hasData, error, onRetry, children }) => (
  <Layout>
    <Loader isVisible={loading && !hasData} />
    <div className="bg-[#F8FAFC] min-h-screen">
      <header className="sticky top-0 z-40 bg-[#EEF3F7] px-6 pt-6 pb-4 flex flex-col gap-4">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="m-0 text-2xl font-bold text-[#111827]">{title}</h1>
            {subtitle && <p className="m-0 text-sm text-gray-600">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-3">{filters}</div>
      </header>
      <main className="max-w-[1600px] mx-auto p-6 flex flex-col gap-6">
        {error ? (
          <ErrorScreen
            title={`Unable to load the ${title}`}
            message={errorMessage(error, 'Something went wrong while loading this report.')}
            onRetry={onRetry}
            isRetrying={loading}
          />
        ) : (
          hasData && (
            <div className={`flex flex-col gap-6 transition-opacity ${loading ? 'opacity-60' : ''}`}>
              {children}
            </div>
          )
        )}
      </main>
    </div>
  </Layout>
);

/** A labelled native select styled like the report's dropdowns. */
export const FilterSelect = ({ label, value, onChange, children, minWidth = 200 }) => (
  <label className="flex flex-col">
    <span className="sr-only">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[44px] px-4 pr-9 border border-[#526477] rounded-md bg-white text-[15px] font-bold text-[#111827]"
      style={{ minWidth }}
    >
      {children}
    </select>
  </label>
);

/** "Result for …" banner: the headline findings, each a coloured dot and a sentence. */
export const ResultBanner = ({ title, items }) => (
  <section aria-label="Result" className="bg-white rounded-2xl shadow-md px-6 py-5 flex flex-col gap-3.5">
    <h2 className="m-0 text-base font-bold text-gray-900">{title}</h2>
    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
      {items.map((item) => (
        <div key={item.key} className="flex gap-3 items-start">
          <span
            aria-hidden="true"
            className={`shrink-0 w-2.5 h-2.5 mt-1.5 rounded-full ${
              item.tone === 'bad' ? 'bg-red-700' : item.tone === 'warn' ? 'bg-amber-700' : 'bg-green-700'
            }`}
          />
          <p className="m-0 text-[15px] leading-snug text-gray-900">{item.text}</p>
        </div>
      ))}
    </div>
  </section>
);

export const CardGrid = ({ children, min = 240 }) => (
  <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))` }}>
    {children}
  </div>
);

export default ReportShell;
